<template>
  <div class="role-page">
    <header class="role-hero">
      <div>
        <p class="eyebrow">Caisse</p>
        <h1>Avances employés</h1>
        <p>Choisissez un employé, saisissez le montant et validez. La date et l'heure sont enregistrées automatiquement.</p>
      </div>
      <span class="hero-count">{{ staffList.length }} employé{{ staffList.length > 1 ? 's' : '' }}</span>
    </header>

    <section class="role-card search-card">
      <label>
        <span>Rechercher</span>
        <input v-model="search" class="form-input search-input" type="search" placeholder="Nom ou prénom" />
      </label>
      <button class="btn-secondary refresh-btn" @click="fetchStaff">Actualiser</button>
    </section>

    <section class="staff-grid">
      <template v-for="staff in filteredStaff" :key="staff.id">
        <button
          class="staff-card"
          :class="{ active: selectedStaff && selectedStaff.id === staff.id }"
          @click="selectStaff(staff)"
        >
          <span class="staff-main">
            <img v-if="staff.pic" :src="staff.pic" alt="" class="avatar" />
            <span v-else class="avatar avatar-fallback">{{ staffInitials(staff) }}</span>
            <span>
              <strong>{{ staffName(staff) }}</strong>
              <small>Employé affecté</small>
            </span>
          </span>
          <span class="select-hint">{{ selectedStaff && selectedStaff.id === staff.id ? 'Ouvert' : 'Choisir' }}</span>
        </button>

        <article v-if="selectedStaff && selectedStaff.id === staff.id" class="role-card selected-card">
          <div class="staff-line">
            <img v-if="selectedStaff.pic" :src="selectedStaff.pic" alt="" class="avatar" />
            <span v-else class="avatar avatar-fallback">{{ staffInitials(selectedStaff) }}</span>
            <div>
              <h2>{{ staffName(selectedStaff) }}</h2>
              <p>{{ currentDateTimeLabel }}</p>
            </div>
          </div>
          <form class="advance-form" @submit.prevent="addAdvance">
            <label>
              <span>Montant (DH)</span>
              <input v-model.number="amount" class="form-input amount-input" type="number" min="1" step="1" placeholder="0" inputmode="numeric" />
            </label>
            <button class="btn-primary" :disabled="saving">{{ saving ? 'Enregistrement...' : 'Valider l\'avance' }}</button>
          </form>
          <p v-if="success" class="success">{{ success }}</p>
          <p v-if="error" class="error">{{ error }}</p>
        </article>
      </template>
    </section>

    <div v-if="!loading && !staffList.length" class="empty-state">
      Aucun employé affecté à ce compte caissier.
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppCashierAdvance',
  data() {
    return {
      staffList: [],
      selectedStaff: null,
      search: '',
      amount: null,
      loading: false,
      saving: false,
      error: '',
      success: '',
      now: new Date(),
      timer: null,
    };
  },
  computed: {
    filteredStaff() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.staffList;
      return this.staffList.filter(staff =>
        `${staff.first_name || ''} ${staff.last_name || ''} ${staff.post_name || ''}`.toLowerCase().includes(q)
      );
    },
    currentDateTimeLabel() {
      return this.now.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  mounted() {
    this.fetchStaff();
    this.timer = setInterval(() => {
      this.now = new Date();
    }, 30000);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    staffName(staff) {
      return `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || 'Employé';
    },
    staffInitials(staff) {
      const first = (staff.first_name || '').trim().charAt(0);
      const last = (staff.last_name || '').trim().charAt(0);
      return `${first}${last}`.toUpperCase() || 'AB';
    },
    selectStaff(staff) {
      this.selectedStaff = this.selectedStaff && this.selectedStaff.id === staff.id ? null : staff;
      this.amount = null;
      this.error = '';
      this.success = '';
    },
    async readError(res, fallback) {
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        return data.error || fallback;
      } catch {
        return text || fallback;
      }
    },
    async fetchStaff() {
      this.loading = true;
      this.error = '';
      try {
        const res = await fetch('/api-pointage/cashier-staff', {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(await this.readError(res, 'Chargement impossible'));
        const data = await res.json();
        this.staffList = Array.isArray(data) ? data : [];
        if (this.selectedStaff && !this.staffList.some(st => st.id === this.selectedStaff.id)) {
          this.selectedStaff = null;
        }
      } catch (err) {
        this.error = err.message;
        this.staffList = [];
      } finally {
        this.loading = false;
      }
    },
    async addAdvance() {
      if (!this.selectedStaff) {
        this.error = 'Choisissez un employé.';
        return;
      }
      if (!this.amount || this.amount <= 0) {
        this.error = 'Montant non valide.';
        return;
      }
      this.saving = true;
      this.error = '';
      this.success = '';
      try {
        const res = await fetch('/api-pointage/addavance', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ staff_id: this.selectedStaff.id, avance: this.amount }),
        });
        if (!res.ok) throw new Error(await this.readError(res, 'Avance refusée'));
        this.success = `Avance de ${Math.round(this.amount)} DH enregistrée.`;
        this.amount = null;
      } catch (err) {
        this.error = err.message;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.role-page {
  width: min(760px, calc(100vw - 32px));
  margin: 28px auto;
}
.role-hero,
.role-card,
.staff-card,
.empty-state {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}
.role-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 24px;
  margin-bottom: 18px;
}
.hero-count,
.select-hint {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: var(--radius-full);
  background: #eef4ff;
  color: var(--primary);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.eyebrow {
  margin: 0 0 6px;
  color: var(--primary);
  font-weight: 800;
  text-transform: uppercase;
  font-size: 12px;
}
h1,
h2,
p {
  margin: 0;
}
.role-hero h1 {
  font-size: 28px;
  margin-bottom: 6px;
}
.role-hero p,
.staff-line p {
  color: var(--gray-600);
}
.role-card {
  padding: 16px;
  margin-bottom: 16px;
}
.search-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: end;
}
label span,
.form-label {
  display: block;
  font-size: 12px;
  font-weight: 800;
  color: var(--gray-700);
  text-transform: uppercase;
  margin-bottom: 8px;
}
.form-input {
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0 14px;
  font: inherit;
  background: white;
}
.selected-card {
  border-color: rgba(37, 99, 235, 0.35);
  background: #f8fbff;
  margin: -4px 0 8px;
}
.staff-line {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}
.avatar {
  width: 54px;
  height: 54px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  object-fit: cover;
  background: var(--gray-50);
}
.avatar-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #edf4ff;
  color: var(--primary);
  font-weight: 900;
}
.advance-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, auto);
  gap: 14px;
  align-items: end;
}
.btn-primary,
.btn-secondary {
  min-height: 44px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0 18px;
  font-weight: 800;
  cursor: pointer;
}
.btn-primary {
  color: white;
  background: var(--primary);
  border-color: var(--primary);
}
.btn-primary:disabled {
  opacity: .6;
  cursor: not-allowed;
}
.btn-secondary {
  background: var(--gray-50);
  color: var(--gray-800);
}
.staff-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.staff-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  text-align: left;
  cursor: pointer;
}
.staff-main {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.staff-card.active {
  border-color: var(--primary);
  background: #f8fbff;
}
.staff-card strong,
.staff-card small {
  display: block;
}
.staff-card small {
  margin-top: 4px;
  color: var(--gray-500);
}
.success {
  color: var(--success);
  font-weight: 800;
  margin-top: 10px;
}
.error {
  color: var(--danger);
  font-weight: 800;
  margin-top: 10px;
}
.empty-state {
  padding: 30px;
  text-align: center;
  color: var(--gray-600);
}
@media (max-width: 720px) {
  .role-page {
    width: 100%;
    margin: 0 auto;
    display: grid;
    gap: 12px;
  }

  .role-hero,
  .role-card,
  .staff-card,
  .empty-state {
    border-radius: 10px;
  }

  .role-hero {
    display: block;
    padding: 18px 16px;
    margin-bottom: 0;
  }

  .hero-count {
    margin-top: 14px;
  }

  .role-hero h1 {
    font-size: 26px;
  }

  .role-hero p {
    line-height: 1.45;
  }

  .role-card {
    padding: 14px;
    margin-bottom: 0;
  }

  .search-card,
  .advance-form {
    grid-template-columns: 1fr;
  }

  .refresh-btn {
    display: none;
  }

  .staff-line {
    gap: 12px;
    margin-bottom: 14px;
  }

  .avatar {
    width: 48px;
    height: 48px;
  }

  .staff-card {
    min-height: 72px;
    padding: 12px;
  }

  .select-hint {
    min-height: 30px;
    padding: 0 10px;
  }

  .btn-primary {
    width: 100%;
  }
}
</style>
